/**
 * /kiosk/waiting/:walkInId and /w/:shortId — live waiting-room view.
 *
 * Same component both places: the kiosk shows it on the big screen, the
 * customer pulls it on their phone via QR. Post-A2P the QR becomes an SMS
 * carrying the /w/ link.
 *
 * ─────────────────────────────────────────────────────────────
 * READS ONE DOCUMENT, NOT THE QUEUE
 * ─────────────────────────────────────────────────────────────
 * This used to derive the customer's record from useWalkInQueue, a list
 * query filtered on status and ordered by checkedInAt. That needed a
 * composite index, so a customer's own screen broke whenever the index
 * lagged — and it could only ever see statuses inside the filter.
 *
 * A single-document read needs no index and returns the record in
 * whatever state it is in. Every status below is a state a real customer
 * can be in, so every one of them renders something true.
 *
 * Option A per the routing decision: assigned customers land here too.
 * The QR hand-off matters mid-cut — they want to know when they're done.
 */

import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { getFns } from '../../lib/firebase';
import { useCopy } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { useWalkIn, useQueuePosition } from '../../hooks/useShopState';
import { useIdleReturn } from '../../hooks/useIdleReturn';
import QrHandoff from '../../components/kiosk/QrHandoff';
import { walkInStatus } from '../../lib/walkInStatus';

function Screen({
  dark,
  onInteract,
  children,
}: {
  dark?: boolean;
  onInteract?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={onInteract}
      onTouchStart={onInteract}
      className={`min-h-screen grid place-items-center p-8 text-center ${
        dark ? 'bg-navy text-oncrest-text' : 'bg-page'
      }`}
    >
      <div>{children}</div>
    </div>
  );
}

export default function WaitingView() {
  const { walkInId, shortId } = useParams();
  const copy = useCopy();
  const c = copy.kioskWalkIn.waiting;
  const { t, barbers } = useShopConfig();

  const lookup = useWalkIn(walkInId, shortId);
  const position = useQueuePosition(lookup.state === 'found' ? lookup.walkIn.id : undefined);

  // Only once they're in the chair. A customer still in line may be standing
  // here watching for their turn — returning home under them would be worse
  // than a stale screen.
  const isCutting = lookup.state === 'found' && lookup.walkIn.status === 'cutting';
  const { resetIdleTimer } = useIdleReturn(isCutting);

  const name = (slug: string | null) => barbers.find((b) => b.slug === slug)?.display_name ?? '';
  const portrait = (slug: string | null) => barbers.find((b) => b.slug === slug)?.portrait.avatar;

  if (lookup.state === 'loading') {
    return (
      <Screen>
        <p className="font-serif text-navy text-2xl" role="status" aria-live="polite">
          {t(c.loading)}
        </p>
      </Screen>
    );
  }

  // Genuinely gone — expired past TTL, or a bad link. Should be rare.
  if (lookup.state === 'missing') {
    return (
      <Screen>
        <p className="font-serif text-navy text-2xl">{t(c.notFoundHeadline)}</p>
        <p className="text-slate-body mt-3">{t(c.notFoundSubline)}</p>
      </Screen>
    );
  }

  const me = lookup.walkIn;

  // ── In the chair now ─────────────────────────────────────────────────
  if (me.status === 'cutting' && me.assignedTo) {
    return (
      <Screen dark onInteract={resetIdleTimer}>
        <img
          src={portrait(me.assignedTo)}
          alt=""
          className="w-28 h-28 rounded-full mx-auto border-2 border-gold"
        />
        <p className="font-serif text-4xl mt-6">
          {t(c.readyHeadline, { barberFirstName: name(me.assignedTo) })}
        </p>
        <p className="text-oncrest-soft mt-3 text-xl">{t(c.readySubline)}</p>
        <QrHandoff shortId={me.shortId} />
      </Screen>
    );
  }

  // ── Finished ─────────────────────────────────────────────────────────
  if (me.status === 'done') {
    return (
      <Screen>
        <p className="font-serif text-navy text-3xl">{t(c.doneHeadline)}</p>
        <p className="text-slate-body mt-3">{t(c.doneSubline)}</p>
      </Screen>
    );
  }

  // ── Released: walked out, or never showed ────────────────────────────
  if (me.status === 'left' || me.status === 'no_show') {
    return (
      <Screen>
        <p className="font-serif text-navy text-3xl">{t(c.releasedHeadline)}</p>
        <p className="text-slate-body mt-3">{t(c.releasedSubline)}</p>
      </Screen>
    );
  }

  // ── Waiting ──────────────────────────────────────────────────────────
  // Same resolver as the kiosk confirmation. Phase 1a leaves
  // projectedWaitMinutes null, so the subline says "we'll call your name"
  // rather than inventing a number; Phase 1b is a data change, not a
  // component change.
  const status = walkInStatus(
    {
      assignedBarber: null,
      preferredBarber: me.preferredBarber,
      position,
      minutes: me.projectedWaitMinutes ?? null,
    },
    copy.walkInStatus,
    t,
    name
  );

  return (
    <Screen>
      <p className="kd-kicker text-gold-text text-xs uppercase tracking-[0.18em]">
        {t(c.inLineKicker)}
      </p>
      <p className="font-serif text-navy text-4xl mt-4">{status.headline}</p>
      {status.position && (
        <p className="font-serif text-navy text-2xl mt-3">{status.position}</p>
      )}
      <p className="text-slate-body mt-6 max-w-sm mx-auto">{status.subline}</p>

      <QrHandoff shortId={me.shortId} />

      <div>
        <button
          type="button"
          onClick={async () => {
            const fn = httpsCallable(getFns(), 'releaseBarber');
            await fn({ barberSlug: me.assignedTo ?? '', walkInId: me.id, outcome: 'left' });
          }}
          className="mt-8 border border-navy text-navy rounded-pill px-8 py-3 font-serif"
        >
          {t(c.cancelCta)}
        </button>
      </div>
    </Screen>
  );
}
